import {useDispatch, useSelector} from "react-redux";
import {selectSelectedElement, updateSelectedInteraction} from "../../../app/slices/TrainingSlice";
import {IDialogQuiz} from "../../../models/interactions/DialogQuiz";
import {IAnswer} from "../../../models/Quiz";
import MessageBubbleActorDropdown from "../message_bubble/MessageBubbleActorDropdown";
import React, {CSSProperties} from "react";
import classNames from "classnames";
import {useTranslation} from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import {GetNewGuid} from "../../../helpers/GuidHelper";
import {useTrainingLocalization} from "../../trainingLocalization/TrainingLocalizationHooks";
import {RootState} from "../../../app/Store";
import {selectKnownOutfits} from "../../../app/slices/MetadataSlice";
import {getOutfitPreviewUri} from "../../../models/Outfit";
import {initialPreviewGender} from "../../outfit_picker/outfitPickerModel";
import DialogQuizAnswerBubble from "./DialogQuizAnswerBubble";
import useDialogQuizTails from "./useDialogQuizTails";
import {COMPOSER_RENDER_HEAD, HeadAnchor} from "./dialogQuizTails";
import "./DialogQuizPreview.css";

const MAX_ANSWERS = 5;
const MIN_ANSWERS = 2;

/**
 * How an actor image stands on the stage: its height in container-height units
 * (cqh), capped by a container-width size (cqw) so narrow popups keep the body
 * inside its column; top in cqh.
 */
type ActorImage = { src: string; head: HeadAnchor; height: number; maxWidth: number; top: number };

const composerImage = (src: string): ActorImage => ({src, head: COMPOSER_RENDER_HEAD, height: 152, maxWidth: 120, top: 5});

/** A stock Composer render, shown when the asker has no outfit render. */
const FALLBACK_ACTOR = composerImage("/dialog_quiz/dialog_quiz_actor.png");

/** The interaction's Right Side property: answers to the right of the actor. */
export function isDialogQuizRightSide(dialog: IDialogQuiz): boolean {
    const value = dialog.properties?.find((property) => property.id === "rightSide")?.value;
    return value === true || value === "true";
}

/** The asker's outfit render for the selected take (take outfit, else the preset's). */
function useActorImage(actorId: string): ActorImage {
    const actor = useSelector((state: RootState) => state.training.actors.find((a) => a.id === actorId));
    const takeOutfit = useSelector((state: RootState) =>
        state.training.takes3D
            ?.find((take) => take.id === state.training.selectedTake)
            ?.actors.find((actorState) => actorState.actorId === actorId)?.outfit
    );
    const knownOutfits = useSelector(selectKnownOutfits);
    const outfitName = takeOutfit || actor?.outfit;
    const uri = outfitName ? getOutfitPreviewUri(knownOutfits[outfitName], initialPreviewGender(actor?.gender)) : undefined;

    return uri ? composerImage(uri) : FALLBACK_ACTOR;
}

export default function DialogQuizPreviewBody() {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const dialog = useSelector(selectSelectedElement) as IDialogQuiz;
    const {getNewKey} = useTrainingLocalization();
    const actorOnLeft = isDialogQuizRightSide(dialog);
    const actorImage = useActorImage(dialog.playerGuid);
    const {tails, imageRef, registerBubble, measure} = useDialogQuizTails(
        actorImage.head,
        actorOnLeft,
        dialog.answers.map((answer) => answer.id).join("|") + actorImage.src
    );

    const onChangeDialog = (field: keyof IDialogQuiz, value: any) => {
        dispatch(updateSelectedInteraction({...dialog, [field]: value} as IDialogQuiz));
    };

    const onAddAnswer = () => {
        const newAnswers = [...dialog.answers, {id: GetNewGuid(), correct: false, text: getNewKey()}];
        onChangeDialog("answers", newAnswers);
    }

    const onDeleteAnswer = (answer: IAnswer) => {
        const newAnswers = dialog.answers.filter(a => a.id !== answer.id);
        onChangeDialog("answers", newAnswers);
    }

    const onChangeAnswer = (newAnswer: IAnswer) => {

        const setToFalse = newAnswer.correct;

        dispatch(updateSelectedInteraction({
            ...dialog,
            answers: dialog.answers.map(answer => answer.id === newAnswer.id ? newAnswer : {...answer, correct: setToFalse ? false : answer.correct})
        } as IDialogQuiz));
    }

    const answers = dialog.answers.map((answer) => (
        <DialogQuizAnswerBubble
            key={answer.id}
            ref={registerBubble(answer.id)}
            answer={answer}
            actorOnLeft={actorOnLeft}
            tail={tails[answer.id]}
            canDelete={dialog.answers.length > MIN_ANSWERS}
            onAnswerChange={onChangeAnswer}
            onDelete={() => onDeleteAnswer(answer)}
        />
    ));

    const canAdd = dialog.answers.length < MAX_ANSWERS;

    return <div className={"dialogquiz-stage"} style={{
        "--dq-actor-x": actorOnLeft ? "30%" : "70%",
        "--dq-actor-h": actorImage.height,
        "--dq-actor-max-w": actorImage.maxWidth,
        "--dq-actor-top": actorImage.top,
        "--dq-head-y": actorImage.head.y,
    } as CSSProperties} data-testid="dialog-quiz-stage">
        <div className={classNames("dialogquiz-row", {"dialogquiz-row--actor-left": actorOnLeft})}>
            <div className={"dialogquiz-answers"} onScroll={measure} data-testid="dialog-quiz-answers">
                {answers}
                <button type="button" className={"dialogquiz-add"} onClick={onAddAnswer} disabled={!canAdd}>
                    <span className={"dialogquiz-add__plus"}><AddIcon fontSize={"small"}/></span>
                    {t("dialog_quiz.add_answer")}
                </button>
            </div>

            <div className={"dialogquiz-actor-slot"}>
                <img
                    ref={imageRef}
                    className={"dialogquiz-actor"}
                    src={actorImage.src}
                    alt={""}
                    draggable={false}
                    onLoad={measure}
                    data-testid="dialog-quiz-actor"
                />
                <div className={"dialogquiz-asked-by"}>
                    <span className={"dialogquiz-asked-by__label text-creator-text-sub"}>{t("dialog_quiz.asked_by")}</span>
                    <MessageBubbleActorDropdown
                        label={""}
                        selectedTargetActorId={dialog.playerGuid}
                        onClick={(actorId) => onChangeDialog("playerGuid", actorId)}
                        className={"!h-auto !w-[200px] min-w-0 shrink"}
                        triggerClassName={"dialogquiz-asked-by__trigger"}
                        menuSide={"top"}
                    />
                </div>
            </div>
        </div>
    </div>
}
